// @ts-strict-ignore
import {AttrsSchema, Component, ConfigOptions} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {NAVIGATION_KEY_CODES} from 'common/util/dom';
import {formatText} from 'common/util/format-text';
import {InputSize} from 'common/components/input/types';

import {template} from './template';
import css from './index.inline.styl';

interface RevealableTextEditorJsxProps {
  attrs?: {
    text: string;
    placeholder?: string;
    size?: InputSize;
    'hide-footer'?: boolean;
    'disable-edit'?: boolean;
    'max-chars'?: number;
    'show-hover-state'?: boolean;
  };
  on?: {
    changedText?: (ev: CustomEvent) => void;
    cancel?: () => void;
  };
}

interface TextEditStates {
  currText: string;
  prevText: string;
  isEditing: boolean;
  isChanged: boolean;
}
export class RevealableTextEditor extends Component<TextEditStates, RevealableTextEditorJsxProps[`attrs`]> {
  static get attrsSchema(): AttrsSchema<RevealableTextEditorJsxProps[`attrs`]> {
    return {
      ...super.attrsSchema,
      text: `string`,
      placeholder: `string`,
      'hide-footer': {
        type: `boolean`,
        description: `minimize the editor ui by hiding save and cancel buttons`,
      },
      'disable-edit': {
        type: `boolean`,
      },
      'max-chars': {
        type: `number`,
        default: 1000,
        description: `max number of characters allowed`,
      },
      size: {type: `string`, enum: Object.values(InputSize), default: InputSize.Large},
      'show-hover-state': {
        type: `boolean`,
      },
    };
  }

  get config(): ConfigOptions<TextEditStates> {
    return {
      useShadowDom: true,
      css,
      defaultState: {
        currText: ``,
        prevText: ``,
        isChanged: false,
        isEditing: false,
      },
      template,
      helpers: {
        formatText,
        handleEditingStart: (ev: MouseEvent) => {
          if (ev.target instanceof HTMLAnchorElement) {
            return;
          }
          this.update({isEditing: true});
        },
        handleValueChange: (value: string) => {
          if (this.state.isEditing) {
            this.update({currText: value});
          }
        },

        handleInputInserted: ({elm}) => {
          if (this.state.isEditing) {
            elm.focus();
          }
        },

        handleSaveByKeyPress: (ev) => {
          if (
            (ev.keyCode === NAVIGATION_KEY_CODES.ENTER || ev.key === `Enter`) &&
            !ev.shiftKey &&
            this.attr(`hide-footer`)
          ) {
            this.saveText();
          }
        },

        handleSaveByBlur: () => {
          if (this.attr(`hide-footer`)) {
            this.saveText();
          }
        },

        textChanged: () => this.state.currText !== this.state.prevText,

        handleSaveByButton: () => {
          this.saveText();
        },

        handleCancelEdit: () => {
          this.cancelEdit();
        },
      },
    };
  }

  saveText() {
    if (this.helpers.textChanged()) {
      this.dispatchEvent(
        new CustomEvent(`changedText`, {
          detail: {value: this.state.currText},
        }),
      );
    }
    this.update({isEditing: false, prevText: this.state.currText});
  }

  public cancelEdit() {
    this.update({isEditing: false, currText: this.state.prevText});
    this.dispatchEvent(new CustomEvent(`cancel`));
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    if (attr === `text`) {
      this.update({currText: this.attr(`text`), prevText: this.attr(`text`)});
    }
  }

  connectedCallback() {
    super.connectedCallback();
  }
}

export const RevealableTextEditEl = defineAndMakeJsxFactory<RevealableTextEditorJsxProps>(
  `mp-revealable-text-editor`,
  RevealableTextEditor,
);
export const RevealableTextEditReact = panel2react<RevealableTextEditorJsxProps>(
  `mp-revealable-text-editor`,
  RevealableTextEditor,
);
